import { useIntl } from 'react-intl';
import PropTypes from '../../../../../../../utils/propTypes';
import { connect, useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import * as Actions from '../../../../../../../services/redux/deliveryNotes/actions';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Flex, Space, Tooltip } from 'antd';
import ActiveFilterTags from './components/ActiveFilterTags';
import { NotificationsContext } from '../../../../../../../context/NotificationsContext';
import {
  getPlants,
  getRecipesByPlantAndSupplier,
} from '../../../../../../../services/api/deliveryNote';
import styles from './index.module.scss';
import AccionaTable from '../../../../../../../components/AccionaTable';
import { COLUMNS_HEADERS } from './columns';
import AccionaTableButton from '../../../../../../../components/AccionaTable/AccionaTableButton';
import MashControlBadge from '../MashControlBadge';
import CertifiedDispersionBadge from '../CertifiedDispersionBadge';
import ConcreteQualityBadge from '../ConcreteQualityBadge';
import BatchCompliance from '../BatchCompliance';
import useNormativeApi from '../EditModal/hooks/useNormativeApi';
import useLotification from '../EditModal/hooks/useLotification';
import { useCallback } from 'react';
import AccionaConfirm from '../../../../../../../components/AccionaConfirm';
import TraceabilityTableHeader from './components/TraceabilityTableHeader';

const { actionsWrapper, link } = styles;

const TraceabilityTable = ({
  getDeliveryNotes,
  normative,
  cleanReducer,
  onViewClick,
  onEditClick,
  structuralElements,
  onParamsChange,
  onEditOnBulkClick,
  selectedRows,
  setSelectedRows,
}) => {
  const intl = useIntl();
  const { project } = useOutletContext();
  const [plants, setPlants] = useState();
  const [params, setParams] = useState();
  const [selectedPlant, setSelectedPlant] = useState(plants?.[0]);
  const [filtersOverride, setFiltersOverride] = useState(null);
  const { pushNotification } = useContext(NotificationsContext);
  const { me } = useSelector(({ auth }) => auth);
  const { getProjectStatus } = useNormativeApi();
  const { unBatchItem } = useLotification();
  const [searchParams] = useSearchParams();
  const did = searchParams.get('did');
  const plant = searchParams.get('plant');
  const supplierId = searchParams.get('supplier_id');
  const [didFilter, setDidFilter] = useState(did);
  const [showConfirmModal, setShowConfirmModal] = useState();
  const dispatch = useDispatch();
  const [recipes, setRecipes] = useState([]);
  const columns = useMemo(
    () =>
      COLUMNS_HEADERS({
        recipes,
        structuralElements,
        normative,
        intl,
        version: project?.version,
      }),
    [recipes, structuralElements, normative, intl, project?.version],
  );

  const initialFilters = useMemo(() => {
    return {
      sortable: 'arriving_date-ASC',
      plant: selectedPlant?.plant,
      supplier_id: selectedPlant?.supplier_id,
      docket_number: didFilter || undefined,
    };
  }, [didFilter, selectedPlant]);

  const actions = useCallback(
    (item) => {
      return (
        <>
          <AccionaTableButton
            type="view"
            toolTip={intl.formatMessage({
              id: 'viewAction.traceability',
            })}
            onClick={() => {
              onViewClick(item);
            }}
          />
          <AccionaTableButton
            type="edit"
            toolTip={intl.formatMessage({
              id: 'editAction.traceability',
            })}
            disabled={!me.is_active}
            onClick={() => {
              onEditClick(item);
            }}
          />
          {item.rawData.concrete_batch && (
            <AccionaTableButton
              type="unBatch"
              toolTip={intl.formatMessage({
                id: 'unBatchAction.traceability',
              })}
              disabled={!me.is_active}
              onClick={() => {
                setShowConfirmModal({
                  message: intl.formatMessage({
                    id: 'pages.quality.normative.traceability.list.unbatch.confirm.message',
                  }),
                  onConfirm: () => {
                    setShowConfirmModal();
                    const payload = {
                      deliveryNote: item?.rawData,
                      formValues: {
                        batch_code: 'unbatch',
                      },
                    };
                    getProjectStatus(payload, true).then(() => {
                      unBatchItem(
                        payload.deliveryNote,
                        payload.formValues,
                        true,
                      )
                        .then(() => {
                          pushNotification({
                            message: intl.formatMessage({
                              id: 'pages.quality.normative.traceability.edit.unbatch.notification.success',
                            }),
                            type: 'success',
                          });
                          dispatch(getDeliveryNotes(params));
                        })
                        .catch(() => {
                          pushNotification({
                            message: intl.formatMessage({
                              id: 'pages.quality.normative.traceability.edit.unbatch.notification.error',
                            }),
                            type: 'error',
                          });
                        });
                    });
                  },
                  onCancel: () => {
                    setShowConfirmModal();
                  },
                });
              }}
            />
          )}
        </>
      );
    },
    [
      intl,
      me.is_active,
      onViewClick,
      onEditClick,
      getProjectStatus,
      unBatchItem,
      pushNotification,
      dispatch,
      getDeliveryNotes,
      params,
    ],
  );

  const handleRemoveFilterTag = useCallback(
    (keysToRemove) => {
      const { pid, ...currentFilters } = params?.filters || {};
      const newFilters = { ...currentFilters };
      keysToRemove.forEach((key) => delete newFilters[key]);
      setFiltersOverride({ filters: newFilters, version: Date.now() });
    },
    [params?.filters],
  );

  const handleOnClearFilters = useCallback(() => {
    setParams((prev) => ({
      ...prev,
      filters: {
        sortable: 'arriving_date-ASC',
        plant: selectedPlant,
      },
    }));
    setDidFilter(undefined);
  }, [selectedPlant]);

  const handleOnChange = useCallback(
    ({ filters, pagination: { current, total, pageSize } }) => {
      setParams((prev) => ({
        ...prev,
        filters: { ...filters, pid: project?.id_project },
        size: pageSize,
        page: current,
      }));
    },
    [project?.id_project],
  );

  useEffect(() => {
    onParamsChange(params);
  }, [onParamsChange, params]);

  useEffect(() => {
    setParams((prev) => {
      return {
        ...(prev || {}),
        filters: initialFilters,
      };
    }, []);
  }, [initialFilters]);

  const data = useCallback(
    (data) => {
      return data.map((item) => {
        const concreteBatch = item.concrete_batch_data;
        const tag = concreteBatch?.tag_batch_data;
        return {
          ...item,
          rawData: item,
          control_type: intl.formatMessage({
            id:
              item.control_type === 'Estadístico'
                ? 'control_type.statistic'
                : item.control_type === '100%'
                ? 'control_type.100%'
                : '--',
          }),
          concrete_batch: !concreteBatch?.batch_code ? (
            '--'
          ) : (
            <Space style={{ gap: '5px' }}>
              <strong>{`${
                tag
                  ? `${tag.tag_data.name} ${tag.tag_number}`
                  : concreteBatch?.batch_code
              }`}</strong>
              <small
                className={`${
                  !concreteBatch?.batch_volume_compliance ? 'text-danger' : ''
                }`}
              >
                {` (${concreteBatch?.batch_volume?.toFixed(1)}`}
                {` m`}
                <sup>3</sup>)
              </small>
              <BatchCompliance concreteBatch={concreteBatch} />
            </Space>
          ),
          docket_number: (
            <Flex justify="flex-start" align="center" gap={5}>
              <Link
                to={`../../ocr/scannedDocuments/scannedDocumentsRC/validated/${item.id}`}
                className={link}
              >
                {item.docket_number}
              </Link>
              {item.mash_control && <MashControlBadge />}
              {item.certified_dis && normative === 'coe' && (
                <CertifiedDispersionBadge />
              )}
              {item.concrete_quality && (
                <ConcreteQualityBadge normative={normative} />
              )}
            </Flex>
          ),
          quantity: concreteBatch?.batch_volume ? (
            <span
              className={`${
                !concreteBatch?.batch_volume_compliance ? 'text-danger' : ''
              }`}
            >
              {` ${concreteBatch?.batch_volume?.toFixed(2)}`}
              {` m`}
              <sup>3</sup>
            </span>
          ) : (
            '--'
          ),
          delivered: (
            <>
              {`${item.delivered} m`} <sup>3</sup>
            </>
          ),
          structural_element: item.structural_element || '--',
          detailed_location: (
            <Tooltip
              title={
                item.detailed_location?.length > 25
                  ? item.detailed_location
                  : null
              }
              placement="top"
            >
              <span
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  width: '165px',
                  display: 'block',
                  cursor:
                    item.detailed_location?.length > 25 ? 'pointer' : 'default',
                }}
              >
                {item.detailed_location}
              </span>
            </Tooltip>
          ),
          elements: item.elements,
          concreteBatch: concreteBatch?.batch_code,
          laboratoryNoteData: item.laboratory_note_data,
          batchCompliance: concreteBatch ? (
            <BatchCompliance concreteBatch={concreteBatch} />
          ) : null,
        };
      });
    },
    [intl, normative],
  );

  useEffect(() => {
    const getAllPlants = async () => {
      const { data } = await getPlants(project?.id_project);
      setPlants(data.plantSuppliers);
      const matchedPlant = plant
        ? data.plantSuppliers?.find(
            (p) => p.plant === plant && p.supplier_id === supplierId,
          )
        : undefined;
      setSelectedPlant(matchedPlant || data.plantSuppliers?.[0]);
    };
    if (project?.id_project) {
      getAllPlants();
    }
  }, [plant, supplierId, project?.id_project]);

  useEffect(() => {
    return () => {
      cleanReducer();
    };
  }, [cleanReducer]);

  useEffect(() => {
    const getRecipes = async () => {
      if (!selectedPlant) return;
      const { data } = await getRecipesByPlantAndSupplier(
        project.id_project,
        selectedPlant.plant,
        selectedPlant.supplier_id,
      );
      setRecipes(data);
    };
    getRecipes();
  }, [project?.id_project, selectedPlant]);

  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <div className={`row ${actionsWrapper}`}>
            <div className="col-sm-12">
              <TraceabilityTableHeader
                plants={plants}
                selectedPlant={selectedPlant}
                setSelectedPlant={setSelectedPlant}
                params={params}
                setParams={setParams}
                onParamsChange={onParamsChange}
                setDidFilter={setDidFilter}
                selectedRows={selectedRows}
                normative={normative}
                onEditOnBulkClick={() => {
                  onEditOnBulkClick(selectedRows);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <ActiveFilterTags
            filters={params?.filters}
            onRemove={handleRemoveFilterTag}
            version={project?.version}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <AccionaTable
            actionsColumnWidth={100}
            skipFetchingUntilFilter="plant"
            hideSelectAllCheckbox
            showSizeChanger
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            isSelectionDisabled={(item) => {
              return (
                item.concreteBatch ||
                (selectedRows[0] &&
                  !(item.mash_control === selectedRows[0]?.mash_control))
              );
            }}
            allowClear={
              !!params?.filters &&
              Object.entries(params?.filters).filter(
                ([key, value]) =>
                  !['plant', 'pid', 'sortable'].includes(key) && value,
              ).length > 0
            }
            onClearFilters={handleOnClearFilters}
            onChange={handleOnChange}
            rowHeight="40px"
            title={intl.formatMessage({
              id: 'pages.quality.normative.traceability.list.title',
            })}
            initialFilters={initialFilters}
            columns={columns}
            entity="deliveryNotes"
            getItems={getDeliveryNotes}
            actions={actions}
            dataParseFunction={data}
            filtersOverride={filtersOverride}
          />
        </div>
      </div>
      {showConfirmModal && (
        <AccionaConfirm
          message={showConfirmModal.message}
          onCancel={showConfirmModal.onCancel}
          onConfirm={showConfirmModal.onConfirm}
        />
      )}
    </>
  );
};

TraceabilityTable.propTypes = {
  setSelectedRows: PropTypes.func.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  getDeliveryNotes: PropTypes.func.isRequired,
  cleanReducer: PropTypes.func.isRequired,
  normative: PropTypes.oneOf(['coe', 'ehe']).isRequired,
  onViewClick: PropTypes.func.isRequired,
  onParamsChange: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  structuralElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      structural_element: PropTypes.string,
    }),
  ),
  onEditOnBulkClick: PropTypes.func.isRequired,
};

TraceabilityTable.defaultProps = {
  structuralElements: [],
  selectedRows: undefined,
};

export default connect(() => ({}), {
  getDeliveryNotes: Actions.getDeliveryNotes,
  cleanReducer: Actions.initReducer,
})(TraceabilityTable);
